import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getMyActiveTables } from '../../services/api';
import { selectIsLogged } from '../../store/auth/authReducer';
import { MyTable } from '../../store/types';
import { forgetOpenTable, readOpenTableBeforeReload } from '../../utils/helpers/activeTableRestore';
import { navigationRef } from '../../utils/helpers/navigationService';

/**
 * Puts a player who reloads the page mid-hand straight back at their table. The server holds the
 * seat through the reconnect grace period and resends the table, but the app boots on Home, so
 * without this the player had to go View Tables -> Return while the hand played on without them.
 *
 * Only the table that was open at the reload is restored, and only while the player is still
 * seated there (or, for a tournament, still in one), so a player who reloads on Home stays there.
 */
const ActiveTableRestoreHost = () => {
	const isLogged = useSelector(selectIsLogged);
	const playerId = useSelector((s: any) => s.auth?.user?.id);
	const remembered = useRef(readOpenTableBeforeReload()).current;
	const done = useRef(!remembered);

	useEffect(() => {
		if (done.current || !remembered || !isLogged || !playerId) return;
		done.current = true;

		getMyActiveTables()
			.then(res => {
				const data = res?.data?.data;
				const tables: MyTable[] = Array.isArray(data?.tables) ? data.tables : [];
				const tournaments: MyTable[] = Array.isArray(data?.tournaments) ? data.tournaments : [];

				const sameId = (t: MyTable) => String(t.id) === String(remembered.tableId);
				// A tournament is entered by its tournament id but listed by the table it seats the
				// player at, so fall back to any tournament table the player is still in.
				const table = remembered.isTournamentRoom
					? (tournaments.find(sameId) ?? tournaments[0])
					: tables.find(sameId);

				if (!table) {
					forgetOpenTable(remembered.token);
					return;
				}
				if (!navigationRef.isReady() || navigationRef.getCurrentRoute()?.name === 'Game') return;

				navigationRef.navigate('Game', {
					tableId: table.id,
					legacyRoomId: table.legacyRoomId ?? remembered.legacyRoomId,
					roomSlug: sameId(table) ? remembered.roomSlug : undefined,
					tableName: sameId(table) ? remembered.tableName : undefined,
					isTournamentRoom: !!remembered.isTournamentRoom,
				});
			})
			.catch(() => {
				// Not reachable or not logged in after all: the player stays on Home, as before.
			});
	}, [isLogged, playerId, remembered]);

	return null;
};

export default ActiveTableRestoreHost;
